import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SocketProvider } from './contexts/SocketContext';
import { AuthProvider } from './contexts/AuthContext';
import { OrgProvider } from './contexts/OrgContext';
import RequireAdmin from './components/RequireAdmin';
import RequireSuperAdmin from './components/RequireSuperAdmin';
import AdminShell from './admin/AdminShell';
import AdminLoginPage from './admin/AdminLoginPage';
import DashboardPage from './admin/DashboardPage';
import OrdersPage from './admin/OrdersPage';
import StatsPage from './admin/StatsPage';
import LabelEditorPage from './admin/LabelEditorPage';
import SettingsPage from './admin/SettingsPage';
import ReportsPage from './admin/ReportsPage';
import SuperAdminPage from './admin/SuperAdminPage';
import UcsQueuePage from './admin/UcsQueuePage';
import MagazynPage from './admin/MagazynPage';
import StationApp from './station/StationApp';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dashboard/tryb ściany to widoki „na żywo". Polling (refetchInterval w hookach)
      // domyślnie ZATRZYMUje się, gdy karta traci widoczność (TV/kiosk/drugi monitor),
      // a refetchOnWindowFocus=false nie wznawia go po powrocie — przez to dane zamarzały
      // i wymagały ręcznego odświeżenia. Wymuszamy odświeżanie również w tle oraz po
      // odzyskaniu fokusu/połączenia, żeby cała aplikacja aktualizowała się sama.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: true,
      retry: 1,
    },
  },
});

function AdminArea() {
  return (
    <RequireAdmin>
      <AdminShell>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/statistics" element={<StatsPage />} />
          <Route path="/labels" element={<LabelEditorPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/ucs" element={<UcsQueuePage />} />
          <Route path="/magazyn" element={<MagazynPage />} />
          <Route path="/super-admin" element={<RequireSuperAdmin><SuperAdminPage /></RequireSuperAdmin>} />
        </Routes>
      </AdminShell>
    </RequireAdmin>
  );
}

function AppContent() {
  const location = useLocation();
  if (location.pathname.startsWith('/packing-station')) {
    return (
      <Routes>
        <Route path="/packing-station/*" element={<StationApp />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/*" element={<AdminArea />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <OrgProvider>
            <SocketProvider>
              <AppContent />
            </SocketProvider>
          </OrgProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
